import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValidRecovery, setIsValidRecovery] = useState(false);

  useEffect(() => {
    // Verificar se há um hash de recuperação na URL
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');

    console.log('Reset Password - Hash params:', { accessToken: !!accessToken, type });

    if (accessToken && type === 'recovery') {
      setIsValidRecovery(true);
      // Limpar o hash da URL para evitar loops
      window.history.replaceState(null, '', window.location.pathname);
    } else {
      // Se não houver token de recuperação na URL, verificar se há uma sessão de recuperação
      const checkSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();

        // Verificar se é uma sessão de recuperação válida
        if (session?.user) {
          // Se há uma sessão, assumir que é válida (veio do link de email)
          setIsValidRecovery(true);
        } else {
          setError('Link de recuperação inválido ou expirado. Por favor, solicite um novo link.');
        }
      };
      checkSession();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validações
    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess(true);

      // Fazer logout para limpar a sessão de recuperação
      await supabase.auth.signOut();

      // Redirecionar para login após 2 segundos
      setTimeout(() => {
        navigate('/signin', { replace: true });
      }, 2000);
    } catch (err) {
      console.error('Password update error:', err);
      setError(err instanceof Error ? err.message : 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  // Mostrar loading inicial enquanto valida
  if (!isValidRecovery && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-salmon mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando link de recuperação...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{ backgroundImage: 'url(/images/login-background.jpg)' }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-nk-black/20" />

        <div className="max-w-md w-full space-y-8 relative z-10">
          <div className="bg-white p-8 rounded-xl shadow-[var(--shadow-3)] text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 rounded-full p-3">
                <CheckCircle className="w-12 h-12 text-nk-success" />
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>
              Senha redefinida com sucesso!
            </h2>
            <p className="text-gray-600 mb-4">
              Você será redirecionado para a página de login em alguns segundos...
            </p>
            <button
              onClick={() => navigate('/signin')}
              className="text-salmon hover:text-salmon-dark font-medium"
            >
              Ir para login agora
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
        style={{ backgroundImage: 'url(/images/login-background.jpg)' }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-nk-black/20" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/images/logo-nankin.svg"
              alt="Nankin Logo"
              className="h-20 w-auto"
            />
          </div>

          <h2 className="mt-6 text-center text-3xl font-bold" style={{ color: 'var(--color-text)' }}>
            Redefinir senha
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--color-text-light)' }}>
            Digite sua nova senha abaixo
          </p>
        </div>

        {error && (
          <div className="bg-nk-error/10 border border-red-200 text-red-700 px-4 py-3 rounded relative flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow-[var(--shadow-3)]" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                Nova senha
              </label>
              <div className="mt-1 relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--color-text-light)' }} />
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-all"
                  style={{
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                  placeholder="••••••••"
                  onFocus={(e) => e.target.style.borderColor = 'var(--salmon)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
              </div>
              <p className="mt-1 text-xs" style={{ color: 'var(--color-text-light)' }}>
                Mínimo de 6 caracteres
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                Confirmar nova senha
              </label>
              <div className="mt-1 relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--color-text-light)' }} />
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full pl-10 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-all"
                  style={{
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                  placeholder="••••••••"
                  onFocus={(e) => e.target.style.borderColor = 'var(--salmon)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="btn-outline-sm flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-coral-deep hover:bg-coral-press focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{
                backgroundColor: loading ? 'var(--salmon-muted)' : 'var(--salmon)'
              }}
            >
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;